import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import { styled } from '@mui/material/styles'
import { TypographyProps } from '@mui/material/Typography'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { cannot } from '@/app/helpers'
import { selectToken } from '@/app/module/auth/store/selectors'
import WhatsappActions from '@/app/module/common/whatsapp/convo/whatsapp-actions'
import WhatsappButtonsDialog from '@/app/module/common/whatsapp/convo/whatsapp-buttons-dialog'
import WhatsappFlow from '@/app/module/common/whatsapp/convo/whatsapp-flow'
import WhatsappListDialog from '@/app/module/common/whatsapp/convo/whatsapp-list-dialog'
import WhatsappLocation from '@/app/module/common/whatsapp/convo/whatsapp-location'
import WhatsappText, { TruncateText } from '@/app/module/common/whatsapp/convo/whatsapp-text'
import { getFilesItem } from '@/app/module/files/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { downloadFile } from '@/app/service/util/download'
import {
  TWhatsappActionsContent,
  TWhatsappButtonsContent,
  TWhatsappFlowContent,
  TWhatsappListContent,
  TWhatsappLocationContent,
  TWhatsappMediaContent,
  TWhatsappMessageContent,
} from '@/app/types'

type MessageContent = {
  type: 'message'
  content: TWhatsappMessageContent
}

type MediaContent = {
  type: 'image' | 'audio' | 'video' | 'document'
  content: TWhatsappMediaContent
}

type ButtonsContent = {
  type: 'buttons'
  content: TWhatsappButtonsContent
}

type ListContent = {
  type: 'list'
  content: TWhatsappListContent
}

type LocationContent = {
  type: 'location'
  content: TWhatsappLocationContent
}

type ActionContent = {
  type: 'actions'
  content: TWhatsappActionsContent
}

type FlowContent = {
  type: 'flow'
  content: TWhatsappFlowContent
}

type WhatsappContent =
  | MessageContent
  | MediaContent
  | ButtonsContent
  | ListContent
  | LocationContent
  | ActionContent
  | FlowContent

type Props = {
  variant?: TypographyProps['variant']
  waContent: WhatsappContent
}

const WhatsappRowContent: React.FC<Props> = ({ waContent, variant = 'subtitle1' }) => {
  const dispatch = useDispatch()
  const token = useSelector(selectToken)
  const orgId = useSelector(selectOrgId)
  const [downloading, setDownloading] = React.useState(false)
  const [buttonsOpen, setButtonsOpen] = React.useState(false)
  const [listOpen, setListOpen] = React.useState(false)

  const downloadAttachment = async (url: string, file: string) => {
    try {
      await downloadFile(url, file, true)
    } finally {
      setDownloading(false)
    }
  }

  const handleButtonsClose = () => setButtonsOpen(false)
  const handleListClose = () => setListOpen(false)

  const renderFiles = (files: string[], attachmentType: string) => (
    <TruncateText variant={variant} color="textPrimary">
      {attachmentType} Attachments:&nbsp;&nbsp;
      {files.map((file, i) => (
        <Link
          href="#"
          key={i}
          onClick={() => {
            if (!downloading) {
              setDownloading(true)
              dispatch(getFilesItem({ token, orgId, name: file }))
                .then((res) => {
                  if (res) {
                    downloadAttachment(res.url, file)
                  } else {
                    setDownloading(false)
                  }
                })
                .catch(() => setDownloading(false))
            }
          }}
        >
          {file}
        </Link>
      ))}
    </TruncateText>
  )

  const { content, type } = waContent
  const hasFiles = 'files' in content && !!content.files.length

  switch (type) {
    case 'message':
      return <WhatsappText message={content.message} variant={variant} />

    case 'image':
      return (
        <>
          <WhatsappText message={content.message} variant={variant} />
          {hasFiles && renderFiles(content.files, 'Image')}
        </>
      )

    case 'audio':
      return (
        <>
          <WhatsappText message={content.message} variant={variant} />
          {content.files.length > 0 && renderFiles(content.files, 'Audio')}
        </>
      )

    case 'video':
      return (
        <>
          <WhatsappText message={content.message} variant={variant} />
          {content.files.length > 0 && renderFiles(content.files, 'Video')}
        </>
      )

    case 'document':
      return (
        <>
          <WhatsappText message={content.message} variant={variant} />
          {content.files.length > 0 && renderFiles(content.files, 'Document')}
        </>
      )

    case 'buttons':
      return (
        <ListContainer>
          <WhatsappText message={content.message} variant={variant} />
          <Button variant="contained" onClick={() => setButtonsOpen(true)}>
            View Buttons
          </Button>
          <WhatsappButtonsDialog
            closeAfterTransition
            content={content}
            open={buttonsOpen}
            onClick={handleButtonsClose}
            onClose={handleButtonsClose}
          />
        </ListContainer>
      )

    case 'list':
      return (
        <ListContainer>
          <WhatsappText message={content.message} variant={variant} />
          <TruncateText variant={variant} color="textPrimary">
            Button:&nbsp;&nbsp;
          </TruncateText>
          <Button variant="contained" onClick={() => setListOpen(true)}>
            {content.button}
          </Button>
          <WhatsappListDialog
            closeAfterTransition
            list={content}
            open={listOpen}
            onClick={handleListClose}
            onClose={handleListClose}
          />
        </ListContainer>
      )

    case 'location': {
      return (
        <WhatsappLocation
          color="textPrimary"
          location={content}
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          variant={variant}
        />
      )
    }

    case 'actions': {
      return <WhatsappActions content={content} variant={variant} />
    }

    case 'flow': {
      return <WhatsappFlow content={content} />
    }

    default:
      return cannot(type)
  }
}

const ListContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-start',
})

export default WhatsappRowContent
